import { keepPreviousData } from "@tanstack/react-query";
import { useId, useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import { Button, Card, EmptyState, Loading } from "../../../ui";
import { sizeWords } from "../model/whatsNew";
import { ViewSwitch } from "../parts/ViewSwitch";
import { WhatsNewList } from "../parts/WhatsNewList";

const LAST_VISIT = "last_visit";

/** The most changes the server lists when the reader asks for the rest (workflow 22: "the rest on request"). */
const EVERYTHING = 500;

/**
 * What's new (workflow 22): the net change since the reader's last visit, or since a session of their own began.
 * Grouped by item with the family breadcrumb, returned work first, about eight entries with the rest on request.
 */
export function WhatsNewPage() {
  const compareId = useId();
  const [since, setSince] = useState<string>(LAST_VISIT);
  const [everything, setEverything] = useState(false);
  // The first view lists about eight; a press asks the server for the rest. The list stays on the screen while it is read.
  const news = useApiQuery("whats_new", everything ? { since, limit: EVERYTHING } : { since }, { placeholderData: keepPreviousData });
  const sessions = useApiQuery("list_sessions", { mine: true }, { staleTime: 60_000 });
  const mine = (sessions.data ?? []).slice(0, 6);
  const sinceWords = since === LAST_VISIT ? "since your last visit" : "since that session began";

  let body;
  if (news.isPending) body = <Loading>Working out what is new</Loading>;
  else if (news.isError) {
    body = (
      <Card>
        <EmptyState title="What's new could not be read" action={<Button onClick={() => void news.refetch()}>Try again</Button>}>
          {refusalText(news.error)}
        </EmptyState>
      </Card>
    );
  } else {
    body = (
      <Card aria-labelledby="news-heading">
        <h2 id="news-heading">{news.data.first_visit ? "Your first visit" : `${sizeWords(news.data)} ${sinceWords}`}</h2>
        <WhatsNewList news={news.data} mode="page" since={sinceWords} onShowEverything={everything ? undefined : () => setEverything(true)} loadingEverything={everything && news.isPlaceholderData} />
      </Card>
    );
  }

  return (
    <div className="stack">
      <header className="nv-page__head">
        <h1>What's new</h1>
        <ViewSwitch />
      </header>
      <p className="muted">The net change for you: what was added, changed, removed, validated, sent back or handed over. What was added and then removed is left out, and so is a change that was undone.</p>
      {mine.length > 0 && (
        <div className="nv-filters">
          <div className="nv-filters__field">
            <label htmlFor={compareId}>Compare with</label>
            <select id={compareId} value={since} onChange={(event) => {
                setSince(event.target.value);
                setEverything(false);
              }}
            >
              <option value={LAST_VISIT}>My last visit</option>
              {mine.map((session) => (
                <option key={session.id} value={`session:${session.id}`}>
                  The start of my session{session.item?.name ? ` on ${session.item.name}` : session.new_item_name ? ` on ${session.new_item_name}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {body}
    </div>
  );
}
